import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Text, IconButton, Icon, useReducedMotion } from '@nula/core-ui';

import { displayNameFor } from '@/lib/recordings/attendee-display';
import type { AttendeeView } from '@/lib/recordings/types/attendees';
import { AttendeeCardContent, AvatarFor } from './attendee-card-content';
import styles from './attendee-row.module.css';

export type AttendeeRowProps = {
  attendee: AttendeeView;
  /** Called with the row's email when the user clicks the X remove button. */
  onRemove: (email: string) => void;
  className?: string;
};

const row = cva(styles.row);
const expandedCard = cva(styles.expandedCard);

/**
 * Single attendee row. Collapsed by default — avatar + name + subtitle.
 * Click the toggle button to expand into the C-style rich card with company
 * description, LinkedIn link, location, and email.
 *
 * The "X" remove affordance is only rendered for `source === 'manual'` —
 * calendar-sourced rows are read-only from this surface.
 *
 * No-match (and failed) rows render the display name + a "Couldn't enrich"
 * italic caption. They have no expand affordance — there's no rich card to show.
 */
export function AttendeeRow({ attendee, onRemove, className }: AttendeeRowProps) {
  const [expanded, setExpanded] = useState(false);
  const shouldReduce = useReducedMotion();

  const name = displayNameFor(attendee);
  const subtitle = subtitleFor(attendee);
  const canExpand = attendee.enrichmentStatus === 'enriched';
  const showRemove = attendee.source === 'manual';
  const showNoMatch =
    attendee.enrichmentStatus === 'no_match' ||
    attendee.enrichmentStatus === 'failed';

  return (
    <Flex
      vertical
      className={clsx(row(), className)}
      data-testid="attendee-row"
      data-source={attendee.source}
    >
      <Flex align="center" gap="sm" className={styles.collapsed}>
        {canExpand ? (
          <button
            type="button"
            className={styles.toggleArea}
            aria-expanded={expanded}
            data-testid="attendee-row-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            <AvatarFor attendee={attendee} name={name} size={30} />
            <Flex vertical className={styles.rowText}>
              <Text size="sm" className={styles.rowName}>{name}</Text>
              {subtitle ? (
                <Text size="xs" className={styles.rowSubtitle}>{subtitle}</Text>
              ) : null}
            </Flex>
          </button>
        ) : (
          <Flex align="center" gap="sm" className={styles.noExpandArea}>
            <AvatarFor attendee={attendee} name={name} size={30} />
            <Flex vertical className={styles.rowText}>
              <Text size="sm" className={styles.rowName}>{name}</Text>
              {showNoMatch ? (
                <Text size="xs" className={styles.noMatchNote}>Couldn't enrich</Text>
              ) : null}
            </Flex>
          </Flex>
        )}

        {showRemove ? (
          <IconButton
            size="sm"
            transparent
            aria-label={`Remove ${attendee.email}`}
            onClick={() => onRemove(attendee.email)}
          >
            <Icon name="close" size={14} aria-hidden="true" />
          </IconButton>
        ) : null}
      </Flex>

      <AnimatePresence initial={false}>
        {expanded && canExpand ? (
          <motion.div
            key="expanded"
            initial={shouldReduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={shouldReduce ? { opacity: 1 } : { opacity: 1, height: 'auto' }}
            exit={shouldReduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            data-testid="attendee-row-expanded"
            className={expandedCard()}
          >
            <AttendeeCardContent attendee={attendee} name={name} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Flex>
  );
}

function subtitleFor(a: AttendeeView): string | null {
  if (a.enrichmentStatus !== 'enriched') return null;
  const parts: string[] = [];
  if (a.title) parts.push(a.title);
  if (a.companyName) parts.push(a.companyName);
  return parts.length > 0 ? parts.join(' · ') : null;
}
